import React from "react";
import { AlertTriangle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { PlayerIdentityCell } from "./feedbackOverviewPlayer";

const MAX_VISIBLE_PLAYERS = 8;

function translatePosition(t, position) {
  const key = String(position || "").toLowerCase();
  const map = {
    defense: t("positions.defense", { defaultValue: "Defense" }),
    midfield: t("positions.midfield", { defaultValue: "Midfield" }),
    forward: t("positions.forward", { defaultValue: "Forward" }),
    lateral: t("positions.lateral", { defaultValue: "Lateral" }),
    goalkeeper: t("positions.goalkeeper", { defaultValue: "Goalkeeper" }),
  };
  return map[key] || position;
}

const RpeTrendPlayerSelector = ({
  players = [],
  positionTabs = [],
  activePosition,
  onPositionChange,
  selectedPlayerIds = new Set(),
  showAllByPosition = {},
  onTogglePlayer,
  onToggleShowAll,
  getStyleForPlayer,
  isDarkMode = false,
}) => {
  const { t } = useTranslation();

  const tabPlayers = players.filter((player) => player.position === activePosition);
  const visiblePlayers = [...tabPlayers]
    .sort((a, b) => Number(b.isAtRisk) - Number(a.isAtRisk) || a.name.localeCompare(b.name))
    .slice(0, MAX_VISIBLE_PLAYERS);

  const hiddenCount = Math.max(tabPlayers.length - visiblePlayers.length, 0);
  const showAllActive = Boolean(showAllByPosition[activePosition]);

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="mb-2 flex flex-wrap gap-1">
        {positionTabs.map((position) => {
          const selected = position === activePosition;
          const allShown = Boolean(showAllByPosition[position]);
          return (
            <button
              key={position}
              type="button"
              onClick={() => onPositionChange?.(position)}
              className={`rounded-md px-2 py-1 text-[11px] font-medium transition-colors ${
                selected
                  ? isDarkMode
                    ? "bg-amber-500/20 text-amber-200 ring-1 ring-amber-400/40"
                    : "bg-amber-50 text-amber-700 ring-1 ring-amber-200"
                  : isDarkMode
                  ? "text-gray-400 hover:bg-gray-800 hover:text-gray-200"
                  : "text-gray-500 hover:bg-slate-100 hover:text-gray-800"
              }`}
            >
              {translatePosition(t, position)}
              {allShown ? " •" : ""}
            </button>
          );
        })}
      </div>

      <div className="mb-2 flex items-center justify-between gap-2">
        <p className={`text-[11px] ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
          {t("feedback.rpe_trend_selector_hint", {
            defaultValue: "{{shown}} affichés sur {{total}}",
            shown: visiblePlayers.length,
            total: tabPlayers.length,
          })}
        </p>
        <button
          type="button"
          onClick={() => onToggleShowAll?.(activePosition)}
          className={`rounded-md px-2 py-0.5 text-[11px] font-medium transition-colors ${
            showAllActive
              ? isDarkMode
                ? "bg-emerald-500/15 text-emerald-300 ring-1 ring-emerald-400/30"
                : "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"
              : isDarkMode
              ? "text-gray-300 hover:bg-gray-800"
              : "text-gray-600 hover:bg-slate-100"
          }`}
        >
          {t("feedback.rpe_trend_show_all_players", { defaultValue: "Afficher tous" })}
        </button>
      </div>

      <div className="min-h-0 flex-1 space-y-1 overflow-y-auto pr-1">
        {visiblePlayers.length === 0 ? (
          <p className={`rounded-lg px-3 py-6 text-center text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
            {t("feedback.no_data", { defaultValue: "Aucune donnée disponible" })}
          </p>
        ) : (
          visiblePlayers.map((player) => {
            const isSelected = selectedPlayerIds.has(player.id) || showAllActive;
            const { color } = getStyleForPlayer(player.name);

            return (
              <button
                key={player.id}
                type="button"
                onClick={() => onTogglePlayer?.(player.id)}
                className={`flex w-full items-center gap-2 rounded-lg border px-2 py-1.5 text-left transition-colors ${
                  isSelected
                    ? isDarkMode
                      ? "border-amber-400/40 bg-amber-500/10"
                      : "border-amber-200 bg-amber-50/80"
                    : isDarkMode
                    ? "border-transparent hover:bg-gray-800/80"
                    : "border-transparent hover:bg-slate-100"
                }`}
              >
                <span
                  className="h-2.5 w-2.5 shrink-0 rounded-full"
                  style={{ backgroundColor: isSelected ? color : "transparent", boxShadow: isSelected ? "none" : `inset 0 0 0 1px ${color}` }}
                />
                <div className="min-w-0 flex-1 [&_span]:text-xs [&_span]:font-medium">
                  <PlayerIdentityCell
                    name={player.name}
                    profilePicture={player.profilePicture}
                    isDarkMode={isDarkMode}
                  />
                </div>
                {player.isAtRisk ? (
                  <span
                    title={t("team_overview.risk_mode_at_risk", { defaultValue: "A risque" })}
                    className={`shrink-0 rounded p-0.5 ${isDarkMode ? "text-rose-300" : "text-rose-600"}`}
                  >
                    <AlertTriangle className="h-3.5 w-3.5" />
                  </span>
                ) : null}
              </button>
            );
          })
        )}
      </div>

      {hiddenCount > 0 ? (
        <p className={`mt-2 text-[10px] leading-snug ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
          {t("feedback.rpe_trend_selector_overflow", {
            defaultValue: "+{{count}} autres via « Afficher tous »",
            count: hiddenCount,
          })}
        </p>
      ) : null}
    </div>
  );
};

export default RpeTrendPlayerSelector;
export { MAX_VISIBLE_PLAYERS };
